// ============================================================================
// FICHIER : src/pages/admin-saas/DashboardSaasPage.tsx
// DESCRIPTION : Dashboard Admin SaaS - Vue globale multi-copropriétés + VIGIE
// AUTEUR : Claude (Session 54 + 57 + 61 + 63 + 148)
// DATE : 01/02/2026
// MODIF : Session 148 - Ajout section VIGIE (users, fondateurs, MRR/CA)
// MODIF : Session 164 - Ajout widget monitoring consommation API SENTINELLE
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  Timestamp,
  doc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import { 
  Building2, 
  Users, 
  FileText, 
  AlertTriangle,
  Mail,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
  RefreshCw,
  Database,
  Shield,
  FolderArchive,
  Loader2,
  Star,
  CreditCard,
  TrendingUp,
  Euro,
  UserCheck,
  Zap,
  Save,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface AdresseObject {
  rue?: string;
  codePostal?: string;
  ville?: string;
}

interface CoproprieteResume {
  id: string;
  nom: string;
  adresse?: string;
  membresCS: number;
  coproprietaires: number;
  signalementEnCours: number;
  messagesEnAttente: number;
  derniereActivite?: Date;
}

interface StatsGlobales {
  totalCoproprietes: number;
  totalUtilisateurs: number;
  totalDocuments: number;
  totalSignalements: number;
  signalementsEnCours: number;
  messagesEnValidation: number;
}

interface AlerteAdmin {
  type: 'warning' | 'error' | 'info';
  message: string;
  coproprieteId?: string;
  coproprieteNom?: string;
  lien?: string;
}

interface StorageBackup {
  id: string;
  completedAt?: Timestamp;
  status: string;
}

// Session 148 : Stats VIGIE
interface StatsVigie {
  totalUsers: number;
  usersActifs: number;
  abonnesPayants: number;
  fondateurs: number;
  mrr: number; // Monthly Recurring Revenue en €
  arr: number; // Annual Recurring Revenue estimé
  repartition: {
    fondateurMensuel: number;
    fondateurAnnuel: number;
    normalMensuel: number;
    normalAnnuel: number;
    sansAbonnement: number;
  };
}

// Session 164 : Stats consommation API
interface StatsAPI {
  totalAnalysesMois: number;
  coutEstimeMois: number; // en €
  parType: Record<string, number>;
  joursDansMois: number;
  joursEcoules: number;
  projectionFinMois: number; // nombre d'analyses projetées
  coutProjection: number; // coût projeté fin de mois en €
}

// ============================================
// CONSTANTES TARIFS (Session 148)
// ============================================

const TARIFS = {
  fondateur: { mensuel: 5.90, annuel: 59 },
  normal: { mensuel: 14.90, annuel: 149 }
};

// Session 164 : Coût moyen estimé par analyse SENTINELLE (€)
const COUT_PAR_ANALYSE = 0.035;

// ============================================
// HELPERS
// ============================================

/**
 * Formate l'adresse qu'elle soit string ou objet
 */
const formatAdresse = (adresse: string | AdresseObject | undefined): string => {
  if (!adresse) return '';
  
  if (typeof adresse === 'object') {
    const parts: string[] = [];
    if (adresse.rue) parts.push(adresse.rue);
    if (adresse.codePostal) parts.push(adresse.codePostal);
    if (adresse.ville) parts.push(adresse.ville);
    return parts.join(', ');
  }
  
  return adresse;
};

/**
 * Compte les documents d'une collection de manière sécurisée
 */
const safeCountCollection = async (collectionRef: ReturnType<typeof collection>): Promise<number> => {
  try {
    const snap = await getDocs(collectionRef);
    return snap.size;
  } catch (error) {
    return 0;
  }
};

/**
 * Compte avec requête filtrée de manière sécurisée
 */
const safeCountQuery = async (q: ReturnType<typeof query>): Promise<number> => {
  try {
    const snap = await getDocs(q);
    return snap.size;
  } catch (error) {
    return 0;
  }
};

/**
 * Calcule le nombre de jours depuis un timestamp
 */
const getDaysSince = (timestamp: Timestamp | undefined): number => {
  if (!timestamp) return 999;
  const now = new Date();
  const date = timestamp.toDate();
  const diffMs = now.getTime() - date.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Formate un montant en euros
 */
const formatEuro = (montant: number): string => {
  return montant.toLocaleString('fr-FR', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// ============================================
// COMPOSANT
// ============================================

const DashboardSaasPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [coproprietes, setCoproprietes] = useState<CoproprieteResume[]>([]);
  const [stats, setStats] = useState<StatsGlobales>({
    totalCoproprietes: 0,
    totalUtilisateurs: 0,
    totalDocuments: 0,
    totalSignalements: 0,
    signalementsEnCours: 0,
    messagesEnValidation: 0
  });
  const [alertes, setAlertes] = useState<AlerteAdmin[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // État pour la dernière sauvegarde Storage
  const [lastStorageBackup, setLastStorageBackup] = useState<StorageBackup | null>(null);

  // Session 63 : États migration GED


  // Session 148 : Stats VIGIE
  const [statsVigie, setStatsVigie] = useState<StatsVigie>({
    totalUsers: 0,
    usersActifs: 0,
    abonnesPayants: 0,
    fondateurs: 0,
    mrr: 0,
    arr: 0,
    repartition: {
      fondateurMensuel: 0,
      fondateurAnnuel: 0,
      normalMensuel: 0,
      normalAnnuel: 0,
      sansAbonnement: 0
    }
  });

  // Session 164 : Stats consommation API
  const [statsAPI, setStatsAPI] = useState<StatsAPI>({
    totalAnalysesMois: 0,
    coutEstimeMois: 0,
    parType: {},
    joursDansMois: 30,
    joursEcoules: 1,
    projectionFinMois: 0,
    coutProjection: 0
  });

  // Session 164 : Solde API Claude (saisie manuelle)
  const [soldeAPI, setSoldeAPI] = useState<number | null>(null);
  const [soldeDateMaj, setSoldeDateMaj] = useState<string>('');
  const [soldeInput, setSoldeInput] = useState('');
  const [soldeSaving, setSoldeSaving] = useState(false);

  // Charger la dernière sauvegarde Storage
  const loadLastStorageBackup = async () => {
    try {
      const q = query(
        collection(db, 'backups'),
        where('type', '==', 'storage'),
        where('status', '==', 'completed'),
        orderBy('completedAt', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        setLastStorageBackup({
          id: doc.id,
          ...doc.data()
        } as StorageBackup);
      }
    } catch (err) {
      console.error('Erreur chargement dernier backup Storage:', err);
    }
  };

  // Session 148 : Charger les stats VIGIE
  const loadStatsVigie = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'vigie')
      );
      const snapshot = await getDocs(q);

      let totalUsers = 0;
      let usersActifs = 0;
      let abonnesPayants = 0;
      let fondateurs = 0;
      let mrr = 0;
      const repartition = {
        fondateurMensuel: 0,
        fondateurAnnuel: 0,
        normalMensuel: 0,
        normalAnnuel: 0,
        sansAbonnement: 0
      };

      for (const userDoc of snapshot.docs) {
        const data = userDoc.data();
        totalUsers++;

        if (data.status === 'active') usersActifs++;

        if (data.statutAbonnement === 'actif') {
          abonnesPayants++;

          const typeOffre = data.typeOffre || 'normal';
          const formule = data.formuleAbonnement || 'mensuel';

          if (typeOffre === 'fondateur') {
            fondateurs++;
            if (formule === 'mensuel') {
              repartition.fondateurMensuel++;
              mrr += TARIFS.fondateur.mensuel;
            } else {
              repartition.fondateurAnnuel++;
              mrr += TARIFS.fondateur.annuel / 12;
            }
          } else {
            if (formule === 'mensuel') {
              repartition.normalMensuel++;
              mrr += TARIFS.normal.mensuel;
            } else {
              repartition.normalAnnuel++;
              mrr += TARIFS.normal.annuel / 12;
            }
          }
        } else {
          repartition.sansAbonnement++;
        }
      }

      setStatsVigie({
        totalUsers,
        usersActifs,
        abonnesPayants,
        fondateurs,
        mrr: Math.round(mrr * 100) / 100,
        arr: Math.round(mrr * 12 * 100) / 100,
        repartition
      });
    } catch (err) {
      console.error('Erreur chargement stats VIGIE:', err);
    }
  };

  // Session 164 : Charger les stats consommation API
  const loadStatsAPI = async () => {
    try {
      const now = new Date();
      const debutMois = new Date(now.getFullYear(), now.getMonth(), 1);
      const finMois = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const joursDansMois = finMois.getDate();
      const joursEcoules = Math.max(1, now.getDate());

      // Récupérer tous les quotas pour agréger les logs d'usage
      const quotasSnap = await getDocs(collection(db, 'quotas'));
      
      let totalAnalyses = 0;
      const parType: Record<string, number> = {};

      for (const quotaDoc of quotasSnap.docs) {
        const coproprieteId = quotaDoc.id;
        
        // Lire les sentinelleUsageLogs du mois en cours
        try {
          const logsQuery = query(
            collection(db, `coproprietes/${coproprieteId}/sentinelleUsageLogs`),
            where('timestamp', '>=', Timestamp.fromDate(debutMois)),
            orderBy('timestamp', 'desc')
          );
          const logsSnap = await getDocs(logsQuery);
          
          for (const logDoc of logsSnap.docs) {
            const logData = logDoc.data();
            totalAnalyses++;
            const typeAnalyse = logData.typeAnalyse || 'autre';
            parType[typeAnalyse] = (parType[typeAnalyse] || 0) + 1;
          }
        } catch {
          // Silencieux — la sous-collection peut ne pas exister
        }
      }

      const coutEstime = Math.round(totalAnalyses * COUT_PAR_ANALYSE * 100) / 100;
      const projectionFinMois = Math.round((totalAnalyses / joursEcoules) * joursDansMois);
      const coutProjection = Math.round(projectionFinMois * COUT_PAR_ANALYSE * 100) / 100;

      setStatsAPI({
        totalAnalysesMois: totalAnalyses,
        coutEstimeMois: coutEstime,
        parType,
        joursDansMois,
        joursEcoules,
        projectionFinMois,
        coutProjection
      });
    } catch (err) {
      console.error('Erreur chargement stats API:', err);
    }
  };

  // Session 164 : Charger le solde API sauvegardé
  const loadSoldeAPI = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'config', 'apiSolde'));
      if (configDoc.exists()) {
        const data = configDoc.data();
        setSoldeAPI(data.solde ?? null);
        setSoldeInput(data.solde?.toString() || '');
        if (data.dateMaj) {
          const date = data.dateMaj.toDate();
          setSoldeDateMaj(date.toLocaleDateString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
          }));
        }
      }
    } catch (err) {
      console.error('Erreur chargement solde API:', err);
    }
  };

  // Session 164 : Sauvegarder le solde API
  const saveSoldeAPI = async () => {
    const valeur = parseFloat(soldeInput.replace(',', '.'));
    if (isNaN(valeur) || valeur < 0) return;
    setSoldeSaving(true);
    try {
      await setDoc(doc(db, 'config', 'apiSolde'), {
        solde: valeur,
        dateMaj: Timestamp.now()
      }, { merge: true });
      setSoldeAPI(valeur);
      const now = new Date();
      setSoldeDateMaj(now.toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }));
    } catch (err) {
      console.error('Erreur sauvegarde solde API:', err);
    } finally {
      setSoldeSaving(false);
    }
  };

  // Charger les données
  const loadData = async () => {
    setError(null);
    
    try {
      // Charger dernier backup Storage + stats VIGIE + stats API en parallèle
      loadLastStorageBackup();
      loadStatsVigie();
      loadStatsAPI();
      loadSoldeAPI();
      
      // 1. Récupérer toutes les copropriétés
      const coproSnap = await getDocs(collection(db, 'coproprietes'));
      const coproList: CoproprieteResume[] = [];
      let totalUsers = 0;
      let totalDocs = 0;
      let totalSignalements = 0;
      let signalementsEnCours = 0;
      let messagesEnValidation = 0;
      const alertesTemp: AlerteAdmin[] = [];

      for (const coproDoc of coproSnap.docs) {
        const coproData = coproDoc.data();
        const coproId = coproDoc.id;

        // Compter les membres CS (sous-collection)
        const nbMembresCS = await safeCountCollection(
          collection(db, `coproprietes/${coproId}/membresCS`)
        );

        // Compter les copropriétaires (sous-collection)
        const nbCoproprietaires = await safeCountCollection(
          collection(db, `coproprietes/${coproId}/coproprietaires`)
        );
        totalUsers += nbCoproprietaires + nbMembresCS;

        // Compter les documents (sous-collection)
        const nbDocs = await safeCountCollection(
          collection(db, `coproprietes/${coproId}/documents`)
        );
        totalDocs += nbDocs;

        // Compter les signalements (sous-collection)
        const nbSign = await safeCountCollection(
          collection(db, `coproprietes/${coproId}/signalements`)
        );
        totalSignalements += nbSign;

        // Signalements en cours (sous-collection avec filtre)
        const nbSignEnCours = await safeCountQuery(
          query(
            collection(db, `coproprietes/${coproId}/signalements`),
            where('statut', 'in', ['nouveau', 'en_cours'])
          )
        );
        signalementsEnCours += nbSignEnCours;

        // Messages en validation (sous-collection avec filtre)
        const nbMsgEnValidation = await safeCountQuery(
          query(
            collection(db, `coproprietes/${coproId}/messagesCS`),
            where('statut', '==', 'en_validation')
          )
        );
        messagesEnValidation += nbMsgEnValidation;

        // Alertes
        if (nbMembresCS < 3) {
          alertesTemp.push({
            type: 'warning',
            message: `Conseil Syndical incomplet (${nbMembresCS}/3 membres)`,
            coproprieteId: coproId,
            coproprieteNom: coproData.nom,
            lien: `/admin/conseil-syndical`
          });
        }

        if (nbMsgEnValidation > 0) {
          alertesTemp.push({
            type: 'info',
            message: `${nbMsgEnValidation} message(s) en attente de vote`,
            coproprieteId: coproId,
            coproprieteNom: coproData.nom,
            lien: `/admin/messagerie`
          });
        }

        if (nbSignEnCours > 5) {
          alertesTemp.push({
            type: 'warning',
            message: `${nbSignEnCours} signalements non traités`,
            coproprieteId: coproId,
            coproprieteNom: coproData.nom,
            lien: `/admin/signalements`
          });
        }

        // Formater l'adresse
        const adresseFormatee = formatAdresse(coproData.adresse);

        coproList.push({
          id: coproId,
          nom: coproData.nom || 'Sans nom',
          adresse: adresseFormatee,
          membresCS: nbMembresCS,
          coproprietaires: nbCoproprietaires,
          signalementEnCours: nbSignEnCours,
          messagesEnAttente: nbMsgEnValidation,
          derniereActivite: coproData.updatedAt?.toDate()
        });
      }

      setCoproprietes(coproList);
      setStats({
        totalCoproprietes: coproSnap.size,
        totalUtilisateurs: totalUsers,
        totalDocuments: totalDocs,
        totalSignalements: totalSignalements,
        signalementsEnCours: signalementsEnCours,
        messagesEnValidation: messagesEnValidation
      });
      setAlertes(alertesTemp);

    } catch (error: any) {
      console.error('Erreur chargement dashboard SaaS:', error);
      setError(error.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  // Calcul jours depuis dernière sauvegarde Storage
  const daysSinceStorageBackup = getDaysSince(lastStorageBackup?.completedAt);
  const storageBackupWarning = daysSinceStorageBackup > 7; // Alerte si > 7 jours

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <XCircle className="mx-auto text-red-500 mb-3" size={48} />
        <h2 className="text-lg font-semibold text-red-800 mb-2">Erreur de chargement</h2>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Tableau de bord
          </h1>
          <p className="text-gray-500 mt-1">
            Vue globale de la plateforme Copro Protect
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          <span>Actualiser</span>
        </button>
      </div>

      {/* Alerte sauvegarde Storage si > 7 jours */}
      {storageBackupWarning && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FolderArchive className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="font-medium text-orange-800">
                Sauvegarde Storage recommandée
              </p>
              <p className="text-sm text-orange-600">
                {lastStorageBackup 
                  ? `Dernière sauvegarde des fichiers il y a ${daysSinceStorageBackup} jours`
                  : 'Aucune sauvegarde des fichiers Storage enregistrée'
                }
              </p>
            </div>
          </div>
          <Link
            to="/admin-saas/sauvegardes"
            className="flex items-center gap-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
          >
            <span>Sauvegarder</span>
            <ArrowRight size={16} />
          </Link>
        </div>
      )}

      {/* ============================================ */}
      {/* SESSION 148 : Section VIGIE — Users + Revenus */}
      {/* ============================================ */}

      <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl border border-blue-100 overflow-hidden">
        {/* Header section VIGIE */}
        <div className="px-5 py-4 border-b border-blue-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="text-blue-600" size={20} />
            <h2 className="font-semibold text-gray-900">VIGIE — Copropriétaires autonomes</h2>
          </div>
          <Link
            to="/admin-saas/utilisateurs-vigie"
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium"
          >
            <span>Gérer les utilisateurs</span>
            <ArrowRight size={14} />
          </Link>
        </div>

        <div className="p-5">
          {/* Stats VIGIE — Ligne 1 : Users */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {/* Total users */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{statsVigie.totalUsers}</p>
                  <p className="text-xs text-gray-500">Inscrits</p>
                </div>
              </div>
            </div>

            {/* Users actifs */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="text-emerald-600" size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{statsVigie.usersActifs}</p>
                  <p className="text-xs text-gray-500">Actifs</p>
                </div>
              </div>
            </div>

            {/* Abonnés payants */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{statsVigie.abonnesPayants}</p>
                  <p className="text-xs text-gray-500">Abonnés</p>
                </div>
              </div>
            </div>

            {/* Places fondateurs */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-amber-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Star className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">
                    {statsVigie.fondateurs}<span className="text-sm font-normal text-gray-400">/20</span>
                  </p>
                  <p className="text-xs text-gray-500">Fondateurs</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats VIGIE — Ligne 2 : Revenus */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* MRR */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Euro className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{formatEuro(statsVigie.mrr)}</p>
                  <p className="text-xs text-gray-500">Revenu mensuel</p>
                </div>
              </div>
            </div>

            {/* ARR estimé */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{formatEuro(statsVigie.arr)}</p>
                  <p className="text-xs text-gray-500">Revenu annuel estimé</p>
                </div>
              </div>
            </div>

            {/* Répartition */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs text-gray-500 font-medium mb-2">Répartition abonnés</p>
              <div className="space-y-1 text-xs">
                {statsVigie.repartition.fondateurMensuel > 0 && (
                  <div className="flex justify-between">
                    <span className="text-amber-700">Fondateur mensuel</span>
                    <span className="font-medium">{statsVigie.repartition.fondateurMensuel}</span>
                  </div>
                )}
                {statsVigie.repartition.fondateurAnnuel > 0 && (
                  <div className="flex justify-between">
                    <span className="text-amber-700">Fondateur annuel</span>
                    <span className="font-medium">{statsVigie.repartition.fondateurAnnuel}</span>
                  </div>
                )}
                {statsVigie.repartition.normalMensuel > 0 && (
                  <div className="flex justify-between">
                    <span className="text-blue-700">Normal mensuel</span>
                    <span className="font-medium">{statsVigie.repartition.normalMensuel}</span>
                  </div>
                )}
                {statsVigie.repartition.normalAnnuel > 0 && (
                  <div className="flex justify-between">
                    <span className="text-blue-700">Normal annuel</span>
                    <span className="font-medium">{statsVigie.repartition.normalAnnuel}</span>
                  </div>
                )}
                {statsVigie.abonnesPayants === 0 && (
                  <p className="text-gray-400 italic">Aucun abonné payant</p>
                )}
                {statsVigie.repartition.sansAbonnement > 0 && (
                  <div className="flex justify-between text-gray-400 pt-1 border-t border-gray-100">
                    <span>Sans abonnement</span>
                    <span>{statsVigie.repartition.sansAbonnement}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Session 164 : Monitoring API SENTINELLE */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            {/* Solde API Claude — saisie manuelle */}
            <div className={`bg-white rounded-lg p-4 shadow-sm border ${
              soldeAPI !== null && soldeAPI < 10 ? 'border-red-300' :
              soldeAPI !== null && soldeAPI < 25 ? 'border-amber-300' : 'border-transparent'
            }`}>
              <p className="text-xs text-gray-500 font-medium mb-2">Solde API Claude (USD)</p>
              {soldeAPI !== null ? (
                <div>
                  <p className={`text-xl font-bold ${
                    soldeAPI < 10 ? 'text-red-600' :
                    soldeAPI < 25 ? 'text-amber-600' : 'text-gray-900'
                  }`}>
                    {soldeAPI.toFixed(2)} $
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Mis à jour le {soldeDateMaj}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Non renseigné</p>
              )}
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="text"
                  value={soldeInput}
                  onChange={(e) => setSoldeInput(e.target.value)}
                  placeholder="Ex: 50.69"
                  className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                />
                <button
                  onClick={saveSoldeAPI}
                  disabled={soldeSaving}
                  className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 disabled:opacity-50"
                  title="Enregistrer"
                >
                  {soldeSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                </button>
              </div>
            </div>

            {/* Analyses ce mois */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Zap className="text-indigo-600" size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{statsAPI.totalAnalysesMois}</p>
                  <p className="text-xs text-gray-500">Analyses ce mois</p>
                </div>
              </div>
            </div>

            {/* Coût estimé */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  statsAPI.coutEstimeMois > 10 ? 'bg-red-100' :
                  statsAPI.coutEstimeMois > 5 ? 'bg-amber-100' : 'bg-green-100'
                }`}>
                  <Euro className={`${
                    statsAPI.coutEstimeMois > 10 ? 'text-red-600' :
                    statsAPI.coutEstimeMois > 5 ? 'text-amber-600' : 'text-green-600'
                  }`} size={20} />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{formatEuro(statsAPI.coutEstimeMois)}</p>
                  <p className="text-xs text-gray-500">Coût API estimé ce mois</p>
                </div>
              </div>
            </div>

            {/* Détail par type */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs text-gray-500 font-medium mb-2">Par type d'analyse</p>
              <div className="space-y-1 text-xs">
                {Object.entries(statsAPI.parType)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className="text-gray-600">{type}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                {Object.keys(statsAPI.parType).length === 0 && (
                  <p className="text-gray-400 italic">Aucune analyse ce mois</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* Statistiques copropriétés (existant)         */}
      {/* ============================================ */}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Building2 className="text-emerald-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCoproprietes}</p>
              <p className="text-sm text-gray-500">Copropriétés</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUtilisateurs}</p>
              <p className="text-sm text-gray-500">Utilisateurs CS</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText className="text-purple-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalDocuments}</p>
              <p className="text-sm text-gray-500">Documents</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.signalementsEnCours}</p>
              <p className="text-sm text-gray-500">Signalements actifs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alertes */}
      {alertes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={20} />
            <h2 className="font-semibold text-gray-900">
              Alertes ({alertes.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {alertes.slice(0, 5).map((alerte, index) => (
              <div 
                key={index}
                className={`px-5 py-3 flex items-center justify-between ${
                  alerte.type === 'error' ? 'bg-red-50' :
                  alerte.type === 'warning' ? 'bg-amber-50' : 'bg-blue-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {alerte.type === 'error' ? (
                    <XCircle className="text-red-500" size={18} />
                  ) : alerte.type === 'warning' ? (
                    <AlertTriangle className="text-amber-500" size={18} />
                  ) : (
                    <Clock className="text-blue-500" size={18} />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{alerte.message}</p>
                    {alerte.coproprieteNom && (
                      <p className="text-xs text-gray-500">{alerte.coproprieteNom}</p>
                    )}
                  </div>
                </div>
                {alerte.lien && (
                  <Link
                    to={alerte.lien}
                    className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                  >
                    <span>Voir</span>
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liste des copropriétés */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="text-gray-400" size={20} />
            <h2 className="font-semibold text-gray-900">
              Copropriétés ({coproprietes.length})
            </h2>
          </div>
          <Link
            to="/admin-saas/coproprietes"
            className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
          >
            <span>Voir tout</span>
            <ArrowRight size={14} />
          </Link>
        </div>

        {coproprietes.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="mx-auto text-gray-300 mb-3" size={48} />
            <p className="text-gray-500">Aucune copropriété enregistrée</p>
            <Link
              to="/admin-saas/coproprietes"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <span>Créer une copropriété</span>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Copropriété
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                    Membres CS
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                    Copropriétaires
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                    Signalements
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                    Messages
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {coproprietes.map((copro) => (
                  <tr key={copro.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{copro.nom}</p>
                        {copro.adresse && (
                          <p className="text-sm text-gray-500">{copro.adresse}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        copro.membresCS >= 3 
                          ? 'bg-emerald-100 text-emerald-800' 
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {copro.membresCS}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center text-gray-600">
                      {copro.coproprietaires}
                    </td>
                    <td className="px-5 py-4 text-center">
                      {copro.signalementEnCours > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                          {copro.signalementEnCours}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-center">
                      {copro.messagesEnAttente > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {copro.messagesEnAttente}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Link
                        to={`/admin?copro=${copro.id}`}
                        className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        Accéder
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Card Utilisateurs VIGIE (Session 148) */}
        <Link
          to="/admin-saas/utilisateurs-vigie"
          className="bg-white rounded-xl p-5 shadow-sm border border-blue-200 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Users className="text-blue-600" size={24} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Utilisateurs VIGIE</p>
            <p className="text-sm text-gray-500">{statsVigie.totalUsers} inscrit{statsVigie.totalUsers > 1 ? 's' : ''}</p>
          </div>
          <ArrowRight className="ml-auto text-gray-400" size={20} />
        </Link>

        {/* Card Sauvegardes avec indicateur Storage */}
        <Link
          to="/admin-saas/sauvegardes"
          className={`bg-white rounded-xl p-5 shadow-sm border hover:shadow-md transition-shadow flex items-center gap-4 ${
            storageBackupWarning ? 'border-orange-300' : 'border-gray-100'
          }`}
        >
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            storageBackupWarning ? 'bg-orange-100' : 'bg-slate-100'
          }`}>
            <Database className={storageBackupWarning ? 'text-orange-600' : 'text-slate-600'} size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-gray-900">Sauvegardes</p>
              {storageBackupWarning && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                  !
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {storageBackupWarning 
                ? `Storage : ${daysSinceStorageBackup === 999 ? 'jamais' : `${daysSinceStorageBackup}j`}`
                : 'Gérer les backups Firebase'
              }
            </p>
          </div>
          <ArrowRight className="ml-auto text-gray-400" size={20} />
        </Link>

      </div>

    </div>
  );
};

export default DashboardSaasPage;
